/**
 * Página de Gestión de Clientes
 *
 * CRUD completo de clientes externos con validación de RUT único
 * Usa componentes genéricos del proyecto
 *
 * @module pages/Clientes
 */

import React, { useMemo } from 'react';
import HighlightedText from '../components/common/HighlightedText';
import { useAuth } from '../components/contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import usePaginatedEntity from '../hooks/usePaginatedEntity';
import EntityTable from '../components/common/EntityTable';
import EntityModal from '../components/common/EntityModal';
import FilterPanel from '../components/common/FilterPanel';
import Pagination from '../components/common/Pagination';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { useToast } from '../components/contexts/ToastContext';
import apiService from '../services/apiService';
import { validateRut, sanitizePhone } from '@app/shared';
import { useExportExcel } from '../hooks/useExportExcel';
import Breadcrumb from '../components/common/Breadcrumb';
import ROUTES from '../constants/routes';

/**
 * Componente principal de gestión de clientes
 *
 * @returns {JSX.Element}
 */
function Clientes() {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();

  const {
    data,
    rawData,
    loading,
    pagination,
    filters,
    modalOpen,
    editingItem,
    confirmDialog,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    handleCreate,
    handleEdit,
    handleDelete,
    confirmDelete,
    handleSave,
    closeModal,
    closeConfirm
  } = usePaginatedEntity({
    entityName: 'cliente',
    entityNamePlural: 'clientes',
    fetchFn: apiService.getClientes,
    createFn: apiService.createCliente,
    updateFn: apiService.updateCliente,
    deleteFn: apiService.deleteCliente,
    filterKeys: ['search'],
    messages: {
      createSuccess: 'Cliente creado',
      updateSuccess: 'Cliente actualizado',
      deleteSuccess: 'Cliente eliminado exitosamente'
    }
  });

  const exportToExcel = useExportExcel({
    data: rawData,
    mapFn: (c) => ({ 'RUT': c.rut, 'Razón Social': c.razon_social, 'Email': c.email || '', 'Teléfono': c.telefono || '' }),
    sheetName: 'Clientes',
    columnWidths: [{ wch: 14 }, { wch: 30 }, { wch: 25 }, { wch: 16 }],
    entityLabel: 'clientes'
  });

  // Configuración de columnas para la tabla
  const columns = useMemo(() => [
    {
      key: 'rut',
      label: 'RUT',
      sortable: true,
      title: true,
      render: (row, searchTerm) => (
        <strong><HighlightedText text={row.rut || ''} searchTerm={searchTerm} /></strong>
      )
    },
    {
      key: 'razon_social',
      label: 'Razón Social',
      sortable: true,
      highlightable: true
    },
    {
      key: 'email',
      label: 'Email',
      render: (row, searchTerm) =>
        row.email
          ? <HighlightedText text={row.email} searchTerm={searchTerm} />
          : <span className="text-muted">-</span>
    },
    {
      key: 'telefono',
      label: 'Teléfono',
      render: (row) => row.telefono || <span className="text-muted">-</span>
    },
    {
      key: 'created_at',
      label: 'Fecha Registro',
      sortable: true,
      mobileHidden: true,
      render: (row) => new Date(row.created_at).toLocaleDateString('es-CL')
    },
    { key: 'acciones', label: 'Acciones', align: 'center' }
  ], []);

  /**
   * Valida, normaliza y guarda un cliente
   * Verifica RUT con dígito verificador y teléfono en formato chileno.
   * Si el teléfono es válido, lo normaliza antes de enviarlo al servidor.
   *
   * @param {Object} formData - Datos del formulario del cliente
   * @returns {Promise<void>}
   */
  const handleSaveWithValidation = async (formData) => {
    // Validar RUT
    if (formData.rut && !validateRut(formData.rut)) {
      showToast('El RUT ingresado no es válido. Verifique el formato y dígito verificador.', 'error');
      return;
    }

    // Validar teléfono (solo si se ingresó)
    if (formData.telefono && formData.telefono.trim()) {
      const phoneValidated = sanitizePhone(formData.telefono, { formatted: false, returnEmpty: false });
      if (!phoneValidated) {
        showToast('El teléfono ingresado no es válido. Use formato chileno: +56912345678 o 912345678', 'error');
        return;
      }
      // Formatear el teléfono antes de enviar
      formData.telefono = phoneValidated;
    }

    return handleSave(formData);
  };

  // Configuración de campos para el modal
  const modalFields = useMemo(() => [
    {
      name: 'rut',
      label: 'RUT',
      type: 'text',
      required: true,
      placeholder: 'Ej: 12.345.678-9 o 12345678-9',
      pattern: '^[0-9]{1,2}\\.?[0-9]{3}\\.?[0-9]{3}-?[0-9kK]$',
      title: 'Formato: 12.345.678-9 o 12345678-9',
      maxLength: 12
    },
    {
      name: 'razon_social',
      label: 'Razón Social',
      type: 'text',
      required: true,
      placeholder: 'Nombre o razón social del cliente',
      maxLength: 200
    },
    {
      name: 'email',
      label: 'Email',
      type: 'email',
      required: true,
      placeholder: 'correo@ejemplo.cl',
      maxLength: 100
    },
    {
      name: 'telefono',
      label: 'Teléfono',
      type: 'text',
      placeholder: '+56912345678 o 912345678',
      pattern: '^(\\+?56)?\\s?9\\s?\\d{4}\\s?\\d{4}$',
      title: 'Formato: +56912345678, 912345678 o +56 9 1234 5678',
      maxLength: 50
    },
    {
      name: 'direccion',
      label: 'Dirección',
      type: 'textarea',
      placeholder: 'Dirección completa del cliente'
    }
  ], []);

  // Configuración de filtros
  const filterConfig = useMemo(() => [
    {
      name: 'search',
      type: 'text',
      placeholder: 'Buscar por RUT, razón social o email...',
      icon: 'fa-search'
    }
  ], []);

  return (
    <>
      <Navbar />
      <div className="main-content">
        <Breadcrumb items={[
          { label: 'Inicio', to: ROUTES.HOME },
          { label: 'Clientes' }
        ]} />

        <FilterPanel
          filters={filters}
          filterConfig={filterConfig}
          onFilterChange={handleFilterChange}
          actionConfig={[
            { label: 'Exportar Excel', icon: 'fa-file-excel', variant: 'secondary', onClick: exportToExcel },
            { label: 'Nuevo Cliente', icon: 'fa-plus', variant: 'primary', onClick: handleCreate, show: () => hasPermission('clientes.create') }
          ]}
        />

        <EntityTable
          columns={columns}
          data={data}
          loading={loading}
          searchTerm={filters.search}
          emptyMessage="No hay clientes registrados"
          actions={[
            { icon: 'fa-edit',  className: 'btn-ghost-primary', title: 'Editar',   onClick: handleEdit,   show: () => hasPermission('clientes.update') },
            { icon: 'fa-trash', className: 'btn-ghost-danger',  title: 'Eliminar', onClick: handleDelete, show: () => hasPermission('clientes.delete') },
          ]}
          animateRows={true}
        />

        <Pagination
          currentPage={pagination.page}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleLimitChange}
          loading={loading}
        />

        <EntityModal
          isOpen={modalOpen}
          onClose={closeModal}
          onSave={handleSaveWithValidation}
          title={editingItem ? 'Editar Cliente' : 'Nuevo Cliente'}
          fields={modalFields}
          initialData={editingItem}
        />

        <ConfirmDialog
          isOpen={confirmDialog.open}
          onClose={closeConfirm}
          onConfirm={confirmDelete}
          title="Eliminar Cliente"
          message={`¿Está seguro de eliminar el cliente "${confirmDialog.item?.razon_social}"?`}
          confirmText="Eliminar"
          variant="danger"
        />
      </div>
    </>
  );
}

export default Clientes;
